'use client';
import { PromotionStar as PromotionStarIcon } from '@ahafood/icons';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Card } from '@ahafood/ui/components/card';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import {
  ETABadge,
  RatingBadge,
} from '../../(main)/store/[storeSlug]/components/store-header';
import AiSummaryHighlight from '../../cua-hang/components/ai-summary-highlight';
import type { Store } from '@/api/ahafood';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { getFallbackName } from '@/lib/utils';
import { useAuthStore } from '@/store';

// type OpenHoursType = Record<string, { start_time: number; end_time: number }[]>;

// const formatTime = (minutes: number): string => {
//   const hours = Math.floor(minutes / 60);
//   const mins = minutes % 60;
//   return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
// };

// const getCurrentDayHours = (openHours?: OpenHoursType): string => {
//   if (!openHours) return 'Đóng cửa';
//   const today = new Date().getDay();
//   const dayIndex = today === 0 ? 6 : today - 1;
//   const dayKey = dayIndex.toString();
//   if (!openHours[dayKey]?.length) return 'Đóng cửa';
//   return openHours[dayKey]
//     .map(
//       (range) =>
//         `${formatTime(range.start_time)} - ${formatTime(range.end_time)}`
//     )
//     .join(', ');
// };

// const StoreHours: React.FC<{ openHours?: OpenHoursType }> = ({ openHours }) => {
//   const [currentDayHours, setCurrentDayHours] = useState<string>('Đóng cửa');
//   useEffect(() => {
//     const updateHours = () => {
//       setCurrentDayHours(getCurrentDayHours(openHours));
//     };
//     updateHours();
//     const timer = setInterval(updateHours, 600000);
//     return () => clearInterval(timer);
//   }, [openHours]);
//   return (
//     <div className="flex items-center gap-1 text-xs text-neutral-60">
//       <Clock className="size-3" />
//       <span>{currentDayHours}</span>
//     </div>
//   );
// };

const InactiveBadge = () => (
  <div className="absolute inset-0 bg-neutral-50/50 z-10 size-full rounded-2xl">
    <Badge className="absolute bg-neutral-90 hover:bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 px-3 py-1.5 shadow-md">
      <span className="font-bold text-nowrap hover:text-neutral-90">
        Đóng cửa
      </span>
    </Badge>
  </div>
);

type Props = {
  data: Store;
  className?: string;
  position?: number;
};

export default function NewStoreCard({ data, className, position }: Props) {
  const { user } = useAuthStore();
  const isClosed = data.is_temporary_closed;
  const router = useRouter();
  const routes = useCityRoutes();

  const disabledClasses = isClosed
    ? 'pointer-events-none cursor-not-allowed'
    : '';

  // const distance = (distance: number) => {
  //   return (distance / 1000).toFixed(1);
  // };

  const handleCardClick = () => {
    if (isClosed) return;

    trackEvent('click_item_card', {
      action_time: Math.floor(Date.now() / 1000),
      item_type: 'store_item',
      position: position ?? 0,
      distance: data.distance,
      total_sold: data.total_sold,
      rating: data.rating.average,
      address: data.nearest_branch?.address || data.address,
      store_id: data._id,
      store_name: data.name,
      user_id: user?.cunstomerId,
    });

    if (data.slug) {
      router.push(routes.storeDetail(data.slug));
    }
  };

  return (
    <Card
      onClick={handleCardClick}
      role="button"
      tabIndex={isClosed ? -1 : 0}
      className={cn(
        'relative flex flex-col rounded-[1.4rem] bg-white md:bg-white/50 shadow-md border-none group md:hover:scale-[1.02]',
        'transition-all duration-200 hover:shadow-xl hover:-translate-y-1 cursor-pointer',
        disabledClasses,
        className
      )}
    >
      {/* Image Container with Badges */}
      <div className="relative aspect-[4/3] overflow-hidden m-2 rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent z-10" />
        <Avatar className="size-full rounded-none">
          {isClosed && <InactiveBadge />}
          <AvatarImage
            src={
              data.image_url || `${assetPrefix}/static/images/empty-store.webp`
            }
            alt={data.name}
            className="size-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        </Avatar>

        {/* Freeship Badges - Bottom overlay with liquid glass effect */}
        {data.ai_tags && data.ai_tags.length > 0 && (
          <div className="absolute inset-x-0 bottom-3 px-3 z-20">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide bg-gradient-to-b from-white/30 to-white/10 p-1 rounded-[32px] border border-white/40 ">
              {data.ai_tags.map((tag, index) => (
                <div key={index} className="relative shrink-0 rounded-[28px]">
                  {/* Inner glass container */}
                  <div className="relative rounded-[28px] px-2 py-1 flex items-center gap-2">
                    <PromotionStarIcon className="size-3.5 shrink-0 fill-yellow-40 text-yellow-40" />
                    <span className="text-xs font-semibold text-white whitespace-nowrap">
                      {tag}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-2 p-4 pt-2">
        {/* Status Badges Row */}
        {/* <div className="flex items-center gap-2">
          <Badge className="bg-red-10 px-2 py-0.5 text-xs font-semibold text-red-50 rounded-md">
            <Image
              src="/static/icons/trending-icon.svg"
              alt="Trending"
              className="size-3 mr-1 inline-block"
            />{' '}
            Hot
          </Badge>
          <Badge className="bg-[#A0C03B] hover:bg-[#A0C03B] px-2 py-0.5 text-xs font-semibold text-white rounded-md">
            Giảm mới
          </Badge>
        </div> */}

        {/* Store Name */}
        <h3 className="text-base font-bold text-neutral-90 line-clamp-1 group-hover:text-primary-60 transition-colors">
          {data.name}
        </h3>

        {/* Rating and Stats */}
        <div className="flex md:flex-col items-center md:items-start gap-4 text-sm">
          <RatingBadge rating={data?.rating} total_sold={data?.total_sold} />
          <ETABadge cityId={data.city_id} distance={data.distance} />
        </div>

        {/* Description/Summary */}
        {data.summary_review.general_summary && (
          <div className="border-t border-dashed pt-3">
            <AiSummaryHighlight summary={data.summary_review} />
          </div>
        )}

        {/* Highlight Items */}
        {data.highlight_items && data.highlight_items.length > 0 && (
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {data.highlight_items.slice(0, 3).map((item, index) => (
              <div
                key={index}
                className="flex-none size-20 rounded-lg overflow-hidden bg-neutral-10"
              >
                <Avatar className="size-full rounded-lg">
                  <AvatarImage
                    src={item.image_preview_url}
                    alt={item.name}
                    className="size-full object-cover"
                  />
                  <AvatarFallback className="text-xs">
                    {getFallbackName(item.name)}
                  </AvatarFallback>
                </Avatar>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
